/**
 * One actor-scoped conversation shared by the floating and full-page hosts.
 * Persist complete snapshots; server limits cause an explicit error, never truncation.
 */
export class ChatSession {
    public current: any = null;
    public status: 'idle' | 'saving' | 'saved' | 'error' = 'idle';
    public error: string = '';
    public storageError: string = '';
    private actorId: string = '';
    private csrf: string = '';
    private initialized = false;
    private generation = 0;
    private revision = 0;
    private origin: any = null;
    private listeners = new Set<(snapshot: any, origin?: any) => void>();
    private savedListeners = new Set<(snapshot: any) => void>();
    private memory = new Map<string, any>();
    private pending = new Map<string, number>();
    private deleted = new Map<string, number>();
    private queue: Promise<any> = Promise.resolve();
    private controller: AbortController | null = null;

    public get saving(): boolean {
        return !!this.current && (this.pending.get(this.current.id) || 0) > 0;
    }

    public async initialize(actorId: any, csrf: any = ''): Promise<any> {
        const nextActor = String(actorId || '').trim();
        this.csrf = typeof csrf === 'string' ? csrf : '';
        if (this.initialized && nextActor === this.actorId) return this.current;
        if (this.actorId && nextActor !== this.actorId) {
            this.memory.delete(this.actorId);
            try { this.storage()?.removeItem(this.storageKey()); } catch (_) {}
        }
        this.controller?.abort();
        this.controller = null;
        this.generation++;
        this.revision++;
        this.initialized = true;
        this.actorId = nextActor;
        this.current = null;
        this.origin = null;
        this.pending.clear();
        this.deleted.clear();
        this.status = 'idle';
        this.error = '';
        this.storageError = '';
        if (nextActor) {
            const cached = this.memory.get(nextActor);
            if (cached) this.current = this.copy(cached);
            else {
                try {
                    const raw = this.storage()?.getItem(this.storageKey());
                    const record = raw ? JSON.parse(raw) : null;
                    if (record?.actorId === nextActor && record.snapshot?.id && Array.isArray(record.snapshot.messages)) {
                        this.current = this.normalize(record.snapshot);
                        this.memory.set(nextActor, this.copy(this.current));
                    }
                } catch (_) {
                    this.storageError = '브라우저의 대화 복원 기능을 사용할 수 없습니다. 현재 화면의 대화는 유지됩니다.';
                }
            }
        }
        this.emit();
        return this.current;
    }

    public subscribe(listener: (snapshot: any, origin?: any) => void): () => void {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    public subscribeSaved(listener: (snapshot: any) => void): () => void {
        this.savedListeners.add(listener);
        return () => this.savedListeners.delete(listener);
    }

    /** Select a server history item, or checkpoint a draft, without a server write. */
    public activate(snapshot: any, origin?: any): any {
        this.current = snapshot ? this.normalize(snapshot) : null;
        this.origin = origin;
        this.revision++;
        this.status = 'idle';
        this.error = '';
        this.cache();
        this.emit();
        return this.current;
    }

    public newConversation(origin?: any): any {
        return this.activate({
            id: this.uuid(),
            conversationId: '',
            title: '새 대화',
            preview: '',
            created: new Date().toISOString(),
            messages: [],
            conversationBinding: null,
            conversationState: null,
            executionRequest: null,
            resultReferences: [],
            alsphereConversation: null,
            pendingRequest: null,
            draft: '',
            agentMode: 'auto',
            toolExecutionMode: 'auto'
        }, origin);
    }

    public publish(snapshot: any, origin?: any): Promise<boolean> {
        let item: any;
        try {
            item = this.normalize(snapshot);
        } catch (_) {
            this.status = 'error';
            this.error = '대화 정보를 저장 가능한 형식으로 변환하지 못했습니다.';
            this.emit();
            return Promise.resolve(false);
        }
        this.current = item;
        this.origin = origin;
        const revision = ++this.revision;
        const generation = this.generation;
        const actorId = this.actorId;
        const csrf = this.csrf;
        const deletion = this.deleted.get(item.id) || 0;
        this.cache();
        this.error = '';
        let encoded: string;
        try {
            if (!actorId) throw new Error('대화 세션을 확인하지 못했습니다. 새로고침한 뒤 다시 시도해 주세요.');
            encoded = JSON.stringify(item);
            this.validate(item, encoded);
        } catch (error: any) {
            this.status = 'error';
            this.error = error.message || '대화 이력을 저장하지 못했습니다.';
            this.emit();
            return Promise.resolve(false);
        }

        this.pending.set(item.id, (this.pending.get(item.id) || 0) + 1);
        this.status = 'saving';
        this.emit();
        const task = this.queue.then(async () => {
            if (generation !== this.generation || actorId !== this.actorId
                || deletion !== (this.deleted.get(item.id) || 0)) return false;
            const controller = new AbortController();
            this.controller = controller;
            try {
                const body = new URLSearchParams();
                body.set('item', encoded);
                if (csrf) body.set('chat_csrf_token', csrf);
                const response = await fetch(agentApiPath('/wiz/api/page.agent/save_history'), {
                    method: 'POST', body, credentials: 'same-origin', signal: controller.signal
                });
                if (response.status === 401 && typeof window !== 'undefined' && typeof CustomEvent !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('nanofab-auth-required'));
                }
                let data = await response.json();
                const status = response.ok ? Number(data?.code || 200) : response.status;
                const errorCode = data?.data?.error?.code || data?.error?.code;
                if ((status === 401 && response.status !== 401) || (status === 403 && errorCode === 'CHAT_CSRF_INVALID')) {
                    if (typeof window !== 'undefined' && typeof CustomEvent !== 'undefined') {
                        window.dispatchEvent(new CustomEvent('nanofab-auth-required'));
                    }
                    throw new Error('반디 로그인 상태를 다시 확인해 주세요.');
                }
                if (!response.ok) throw new Error(this.responseMessage(data));
                for (let depth = 0; depth < 4; depth++) {
                    if (data?.code !== undefined && Number(data.code) !== 200) {
                        throw new Error(this.responseMessage(data));
                    }
                    if (!data || typeof data !== 'object' || data.ok !== undefined || data.data === undefined) break;
                    data = data.data;
                }
                if (data?.ok !== true) throw new Error(this.responseMessage(data));
                if (generation !== this.generation || deletion !== (this.deleted.get(item.id) || 0)) return false;
                if (revision === this.revision) {
                    this.status = 'saved';
                    this.error = '';
                }
                for (const listener of this.savedListeners) {
                    try { listener(this.copy(item)); } catch (_) { /* Host render errors do not invalidate a save. */ }
                }
                if (typeof window !== 'undefined' && typeof CustomEvent !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('fab-chat-history-saved', { detail: { id: item.id, actorId } }));
                }
                return true;
            } catch (error: any) {
                if (generation === this.generation && revision === this.revision) {
                    this.status = 'error';
                    this.error = error?.message || '대화 이력을 저장하지 못했습니다. 다시 시도해 주세요.';
                }
                return false;
            } finally {
                if (this.controller === controller) this.controller = null;
            }
        }).finally(() => {
            if (generation === this.generation) {
                const count = (this.pending.get(item.id) || 1) - 1;
                if (count > 0) this.pending.set(item.id, count);
                else this.pending.delete(item.id);
                this.emit();
            }
        });
        // Each write waits for the preceding request, including its failure.
        this.queue = task.catch(() => false);
        return task;
    }

    /** Wait before server-side deletion so an earlier save cannot recreate the item. */
    public async flush(): Promise<void> {
        let pending: Promise<any>;
        do {
            pending = this.queue;
            await pending;
        } while (pending !== this.queue);
    }

    /** Clear the local active item after a successful server deletion. */
    public remove(id?: string, origin?: any): void {
        const target = id ? String(id) : '';
        if (target) this.deleted.set(target, (this.deleted.get(target) || 0) + 1);
        else {
            for (const key of this.pending.keys()) this.deleted.set(key, (this.deleted.get(key) || 0) + 1);
        }
        if (!target || this.current?.id === target) this.activate(null, origin);
    }

    private normalize(snapshot: any): any {
        // PDF image previews and their request controllers belong to the viewer,
        // not the transcript. Remove only these known message-level UI caches.
        const projected = { ...(snapshot || {}) };
        if (Array.isArray(projected.messages)) {
            projected.messages = projected.messages.map((message: any) => {
                if (!message || typeof message !== 'object') return message;
                const { sourceDetails, sourceDetail, pageImage, controller, ...persistent } = message;
                return persistent;
            });
        }
        const value = this.copy(projected);
        value.id = String(value.id || this.current?.id || this.uuid());
        value.conversationId = String(value.conversationId || '');
        value.created = value.created || new Date().toISOString();
        value.messages = Array.isArray(value.messages) ? value.messages : [];
        const question = value.messages.find((message: any) => message?.role === 'user')?.content || '';
        const priorTitle = this.current?.id === value.id ? this.current.title : '';
        const title = value.title || priorTitle;
        value.title = Array.from(String((!title || title === '새 대화') && question ? question : (title || '새 대화'))
            .replace(/\s+/g, ' ').trim()).slice(0, 160).join('') || '새 대화';
        value.preview = Array.from(String(value.messages[value.messages.length - 1]?.content || value.preview || ''))
            .slice(0, 240).join('');
        value.messages.forEach((message: any) => {
            if (message?.attachment?.kind === 'wafer_map') {
                message.attachment = { kind: 'wafer_map', size: Math.max(0, Math.floor(Number(message.attachment.size) || 0)) };
            }
            if (message?.intentReview && (message.intentReview.hasUserData
                || String(message.intentReview.dataSource || '').startsWith('사용자 첨부 데이터'))) {
                message.intentReview.dataSource = '사용자 첨부 데이터';
            }
        });
        return value;
    }

    private validate(item: any, encoded: string): void {
        if (item.messages.length > 100) throw new Error('대화당 메시지는 최대 100개까지 저장할 수 있습니다. 새 대화를 시작해 주세요. 현재 대화 내용은 유지됩니다.');
        if (item.messages.some((message: any) => typeof message?.role !== 'string'
            || (message.content !== undefined && typeof message.content !== 'string'))) {
            throw new Error('대화 메시지 형식이 올바르지 않습니다.');
        }
        if (item.messages.some((message: any) => Array.from(message.content || '').length > 50000)) {
            throw new Error('개별 메시지 본문은 50,000자 이하여야 저장할 수 있습니다. 현재 대화 내용은 유지됩니다.');
        }
        if (new TextEncoder().encode(encoded).byteLength > 512 * 1024) {
            throw new Error('대화 이력 크기는 512KB 이하여야 저장할 수 있습니다. 현재 대화 내용은 유지됩니다.');
        }
    }

    private responseMessage(data: any): string {
        return String(data?.data?.error?.message || data?.data?.data?.message || data?.data?.message
            || data?.error?.message || data?.message || '대화 이력을 저장하지 못했습니다. 다시 시도해 주세요.');
    }

    private storageKey(): string { return 'fab.chat.active.v1:' + encodeURIComponent(this.actorId); }

    private storage(): Storage | null {
        return typeof sessionStorage === 'undefined' ? null : sessionStorage;
    }

    private cache(): void {
        if (!this.actorId) return;
        if (this.current) this.memory.set(this.actorId, this.copy(this.current));
        else this.memory.delete(this.actorId);
        try {
            const storage = this.storage();
            if (this.current) storage?.setItem(this.storageKey(), JSON.stringify({ actorId: this.actorId, snapshot: this.current }));
            else storage?.removeItem(this.storageKey());
            this.storageError = '';
        } catch (_) {
            this.storageError = '브라우저에 대화를 임시 저장하지 못했습니다. 현재 화면의 대화는 유지되지만 새로고침하면 복원되지 않을 수 있습니다.';
        }
    }

    private copy(value: any): any { return value == null ? value : JSON.parse(JSON.stringify(value)); }

    private emit(): void {
        for (const listener of this.listeners) {
            try { listener(this.copy(this.current), this.origin); } catch (_) { /* Keep other hosts subscribed. */ }
        }
    }

    private uuid(): string {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
        return 'chat-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
    }
}

export const chatSession = new ChatSession();

/**
 * Preserve standalone WIZ APIs while supporting BANDI /agent/ proxy.
 */
function agentApiPath(path: string): string {
    if (!path.startsWith('/wiz/api/')) {
        throw new Error('Unexpected agent API path');
    }

    if (typeof window === 'undefined') {
        return path;
    }

    const pathname = window.location.pathname || '';
    const embeddedOnBandi =
        pathname === '/agent' ||
        pathname.startsWith('/agent/');

    return embeddedOnBandi ? '/agent' + path : path;
}
